import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useInRouterContext } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { mediaUrl } from '../../utils/mediaUrl';
import { Avatar } from './PersonName';
import { BODY_ORDER, PrintContext, profileUrlFor, ROLE_ORDER } from './programModel';
import {
  BodyTitle, Grid, GridItem, LogoFallback, LogoFrame, OrgLink, PersonTile, ProfileTile, StaticTile, SubTitle,
  TileInstitution, TileName, TileProfileLink, TileRoles,
} from './programStyles';

const TILE_PHOTO = 110;

// An organization's logo, contain-fitted in a square frame (never cropped round); a building icon
// when there is no logo or it does not load (the name is printed below the frame).
const OrganizationLogo = ({ logoUrl }) => {
  const [failed, setFailed] = useState(false);
  return (
    <LogoFrame $size={TILE_PHOTO} data-testid="organization-logo">
      {logoUrl && !failed
        ? <img src={mediaUrl(logoUrl)} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
        : <LogoFallback aria-hidden="true"><Building2 size={48} /></LogoFallback>}
    </LogoFrame>
  );
};

// Decision 4 of §7.6: below the timeline, every person named in the program (any role) with photo,
// then the organizers and committees. Clicking a person shows their entries in the timeline; a small
// "Profile" link under the tile leads to their public profile. A body member not named in the program
// has nothing to show there, so the whole tile leads to the profile (design study-feed §5.5). The
// print copy carries no links.
const ProgramPeople = ({ people, selection, onSelectPerson }) => {
  const intl = useIntl();
  const printing = useContext(PrintContext);
  const inRouter = useInRouterContext();
  const linkProps = (url) => (inRouter ? { as: Link, to: url } : { href: url });
  const programPeople = people?.people || [];
  const named = new Set(programPeople.map((p) => p.personId));
  const bodies = BODY_ORDER
    .map((body) => (people?.bodies || []).find((b) => b.body === body))
    .filter((b) => b && (b.members || []).length > 0);

  const roleText = (roles) => (roles || [])
    .slice()
    .sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b))
    .map((r) => intl.formatMessage({ id: `program.public.role.${r}`, defaultMessage: r }, { count: 1 }))
    .join(' · ');

  const personTile = (p, roles) => {
    const content = (
      <>
        <Avatar name={p.name} photoUrl={p.photoUrl} size={TILE_PHOTO} />
        <TileName>{p.name}</TileName>
        {p.institution && <TileInstitution>{p.institution}</TileInstitution>}
        {roles && <TileRoles>{roleText(roles)}</TileRoles>}
      </>
    );
    const profileUrl = printing ? null : profileUrlFor(p);
    // Only people named in the program can be shown there; body members who are not lead to their
    // profile, or stay static without one.
    if (p.personId == null || !named.has(p.personId) || !onSelectPerson) {
      return profileUrl ? <ProfileTile {...linkProps(profileUrl)}>{content}</ProfileTile> : <StaticTile>{content}</StaticTile>;
    }
    const active = selection === p.personId;
    return (
      <>
        <PersonTile
          type="button"
          $active={active}
          aria-pressed={active}
          onClick={() => onSelectPerson(active ? null : p.personId)}
        >
          {content}
        </PersonTile>
        {profileUrl && (
          <TileProfileLink {...linkProps(profileUrl)} aria-label={`${intl.formatMessage({ id: 'program.public.person.profile', defaultMessage: 'View profile' })}: ${p.name}`}>
            {intl.formatMessage({ id: 'program.public.person.profile', defaultMessage: 'View profile' })}
          </TileProfileLink>
        )}
      </>
    );
  };

  if (programPeople.length === 0 && bodies.length === 0) return null;

  return (
    <div>
      {programPeople.length > 0 && (
        <>
          <SubTitle>{intl.formatMessage({ id: 'program.public.people.title', defaultMessage: 'Speakers and faculty' })}</SubTitle>
          <Grid>
            {programPeople.map((p) => <GridItem key={p.personId}>{personTile(p, p.roles)}</GridItem>)}
          </Grid>
        </>
      )}
      {bodies.map((b) => (
        <section key={b.body}>
          <BodyTitle>{intl.formatMessage({ id: `program.public.body.${b.body}`, defaultMessage: b.body })}</BodyTitle>
          <Grid>
            {b.members.map((m, i) => (
              <GridItem key={`${m.name}-${i}`}>
                {m.organization ? (
                  m.websiteUrl ? (
                    <OrgLink href={m.websiteUrl} target="_blank" rel="noopener noreferrer">
                      <OrganizationLogo logoUrl={m.logoUrl} />
                      <TileName>{m.name}</TileName>
                    </OrgLink>
                  ) : (
                    <StaticTile>
                      <OrganizationLogo logoUrl={m.logoUrl} />
                      <TileName>{m.name}</TileName>
                    </StaticTile>
                  )
                ) : personTile(m, null)}
              </GridItem>
            ))}
          </Grid>
        </section>
      ))}
    </div>
  );
};

export default ProgramPeople;
